<template>
  <div class="main">
    <a href="/">
      <img class="logo-auth d-block mx-auto mb-3" src="/assets/images/Explore2Teach_Logo.png" alt="" />
    </a>
    <section class="signup">
      <div class="container sign-up-container">
        <div class="signup-content">
          <div class="signup-form">
            <h2 class="form-title mb-0">Study in the U.S.</h2>
            <p class="mt-2">Please share your details and we'll take you to the next step.</p>

            <form @submit.prevent="saveStudentDetails" class="mt-2">
              <div class="row">
                <div class="col-12 col-md-6">
                  <div class="form-group mb-4">
                    <label class="form-label" for="first_name">First Name</label>
                    <input type="text" v-model="firstName" class="form-control" placeholder="First Name"
                      id="first_name" required />
                  </div>
                </div>
                <div class="col-12 col-md-6">
                  <div class="form-group mb-4">
                    <label class="form-label" for="last_name">Last Name</label>
                    <input type="text" v-model="lastName" class="form-control" placeholder="Last Name" id="last_name"
                      required />
                  </div>
                </div>
              </div>
              <div class="form-group mb-4">
                <label class="form-label" for="email">Email</label>
                <input type="email" v-model="email" class="form-control" placeholder="hello@example.com" id="email"
                  required />
              </div>
              <div class="form-group mb-4">
                <label class="form-label" for="contact_number">Contact Number</label>
                <br />
                <MazPhoneNumberInput orientation="row" v-model="contactNumber" country="US" ref="contactNumberInput"
                  class="w-100" @update="onPhoneUpdate" />
                <p v-if="contactNumberError" class="text-danger">
                  {{ contactNumberError }}
                </p>
              </div>
              <div class="form-group mb-4">
                <label class="form-label">Country</label>
                <select v-model="selectedCountry" class="form-control">
                  <option value="" disabled>Select your country</option>
                  <option v-for="country in countryList" :key="country.name" :value="country.name">
                    {{ country.name }}
                  </option>
                </select>
              </div>
              <div class="form-group mb-4">
                <label class="form-label" for="educational_background">Educational Background</label>
                <select v-model="educationalBackground" class="form-control" id="educational_background" required>
                  <option value="" disabled>Select your educational background</option>
                  <option value="Education Graduate">Education Graduate</option>
                  <option value="Teacher">Teacher</option>
                  <option value="Career Shifter">Career Shifter</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="form-group mb-4">
                <label class="form-label" for="interested_to_study">Interested to study in the U.S?</label>
                <select v-model="interestedToStudy" class="form-control" id="interested_to_study" required>
                  <option value="" disabled>Select an option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div class="text-center">
                <p v-if="errorMessage" class="text-danger">
                  {{ errorMessage }}
                </p>
                <div class="d-flex justify-content-center gap-2 mb-2">
                  <button type="submit" class="btn btn-primary" :disabled="isSaving">
                    <span v-if="isSaving">Saving...</span>
                    <span v-else>Save &amp; Continue</span>
                  </button>

                  <button type="button" @click="cancel" class="btn btn-outline-secondary" :disabled="isSaving">
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
          <div class="signup-image m-0 d-flex flex-column justify-content-center align-items-center">
            <figure>
              <img src="/assets/images/home-banner/470691537_3778467092371094_2360789783421571428_n.png"
                alt="study in the us image" class="img-fluid" />
            </figure>
            <a href="#" class="signup-image-link text-center mt-3">
              Ready to begin your journey to the U.S.?
            </a>
            <p class="text-center fs-5 fw-semibold mt-2">Follow us</p>
              <div class="d-flex justify-content-center gap-4">
              <a href="https://www.facebook.com/explore2teach" target="_blank" class="text-primary fs-1">
                <i class="fa-brands fa-facebook"></i>
              </a>
              <a href="https://www.instagram.com/explore2teach/" target="_blank" class="text-danger fs-1">
                <i class="fa-brands fa-instagram"></i>
              </a>
              <a href="https://www.linkedin.com/company/explore2teach" target="_blank" class="text-primary fs-1">
                <i class="fa-brands fa-linkedin"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { doc, setDoc, collection, serverTimestamp } from "firebase/firestore";
import { firestore } from "./../firebase";
import MazPhoneNumberInput from "maz-ui/components/MazPhoneNumberInput";
import "maz-ui/styles"; // or import 'maz-ui/css/main.css'
import { countries } from "@/utils/countries"; // adjust path as needed

export default {
  name: "StudyInUS",
  components: {
    MazPhoneNumberInput,
  },
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      contactNumber: "",
      contactNumberError: "",
      selectedCountry: "United States", // Default country
      educationalBackground: "",
      interestedToStudy: "",
      countryList: [],
      isSaving: false,
      errorMessage: "",
    };
  },
  mounted() {
    this.fetchCountries();
  },
  methods: {
    fetchCountries() {
      try {
        const list = countries();
        this.countryList = list
          .map((country) => ({
            name: country.text,
            code: country.value,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        console.error("Error loading countries from utils:", error);
      }
    },
    onPhoneUpdate(payload) {
      if (this.contactNumber && payload && payload.isValid === false) {
        this.contactNumberError = "Please enter a valid phone number";
      } else {
        this.contactNumberError = "";
      }
    },
    async saveStudentDetails() {
      if (this.isSaving) return;
      this.errorMessage = "";

      if (this.contactNumberError) {
        return;
      }

      if (
        !this.firstName.trim() ||
        !this.lastName.trim() ||
        !this.email.trim() ||
        !this.contactNumber ||
        !this.selectedCountry ||
        !this.educationalBackground ||
        !this.interestedToStudy
      ) {
        this.errorMessage = "All fields are required.";
        return;
      }

      this.isSaving = true;
      try {
        // Pre-generate the document reference to obtain the auto-generated id.
        const docRef = doc(collection(firestore, "F1StudentDetails"));

        const studentData = {
          studentId: docRef.id,
          firstName: this.firstName.trim(),
          lastName: this.lastName.trim(),
          email: this.email.trim(),
          contactNumber: this.contactNumber,
          country: this.selectedCountry,
          educationalBackground: this.educationalBackground,
          interestedToStudy: this.interestedToStudy,
          dateCreated: serverTimestamp(),
        };

        await setDoc(docRef, studentData);

        // Redirect to the Canva site after saving.
        window.location.href = "https://explore2teach.my.canva.site/";
      } catch (error) {
        console.error("Error saving F1 student details:", error);
        this.errorMessage =
          "Something went wrong while saving your details. Please try again.";
      } finally {
        this.isSaving = false;
      }
    },
    cancel() {
      this.$router.push("/");
    },
  },
};
</script>

<style>
display-flex,
.display-flex,
.display-flex-center,
.signup-content,
.signin-content,
.social-login,
.socials {
  display: flex;
  display: -webkit-flex;
}

list-type-ulli,
.socials {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

a:focus,
a:active {
  text-decoration: none;
  outline: none;
  transition: all 300ms ease 0s;
}

img {
  max-width: 100%;
  height: auto;
}

figure {
  margin: 0;
}

p {
  margin-bottom: 0px;
  font-size: 15px;
  color: #777;
}

h2 {
  line-height: 1.66;
  margin: 0;
  padding: 0;
  font-weight: bold;
  color: #222;
  font-family: Poppins;
  font-size: 36px;
}

.main {
  background: #f8f8f8;
  padding: 10px 0;
}

.logo-auth {
  width: 150px;
}

@media only screen and (max-width: 35.9375rem) {
  .logo-auth {
    width: 90px;
  }
}

.container {
  width: 900px;
  background: #fff;
  margin: 0 auto;
  box-shadow: 0px 15px 16.83px 0.17px rgba(0, 0, 0, 0.05);
  border-radius: 20px;
}

.display-flex {
  justify-content: space-between;
  align-items: center;
}

.display-flex-center {
  justify-content: center;
  align-items: center;
}

.signup {
  margin-bottom: 150px;
}

.signup-content {
  padding: 75px 0;
}

.signup-form,
.signup-image {
  width: 50%;
  overflow: hidden;
}

.signup-image {
  margin: 0 55px;
  margin-top: 45px;
}

.form-title {
  margin-bottom: 33px;
}

figure {
  margin-bottom: 10px;
  text-align: center;
}

.signup-image-link {
  font-size: 14px;
  color: #222;
  display: block;
  text-align: center;
}

.signup-form {
  margin-right: 75px;
  padding-left: 34px;
}

.m-input-wrapper {
  width: 100%;
}

.iti {
  display: block !important;
  width: 100% !important;
}

.iti input {
  width: 100% !important;
}

@media screen and (max-width: 1200px) {
  .container {
    width: calc(100% - 30px);
    max-width: 100%;
  }
}

@media screen and (min-width: 1024px) {
  .container {
    max-width: 1200px;
  }
}

@media screen and (max-width: 768px) {
  .sign-up-container {
    padding: 0px;
  }

  .sign-up-container .signup-content {
    padding: 20px 0px;
  }

  .signup-content {
    flex-direction: column;
    justify-content: center;
  }

  .signup-form {
    margin-left: 0px;
    margin-right: 0px;
    padding: 0 30px;
  }

  .signup-form,
  .signup-image {
    width: auto;
  }

  .form-title {
    text-align: center;
  }
}
</style>
